import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltip } from "@/components/ChartTooltip";
import { AXIS_TICK, CHART_MARGIN, legendText } from "@/components/chartChrome";
import { ChartEmptyState } from "@/components/states";
import { formatChartLabel, formatMonthDay } from "@/lib/format";

/**
 * Two series of the SAME unit on one axis (the honest comparison — never a
 * dual-axis chart), daily x-axis.
 */
export function TwoSeriesTimeChart({
  data,
  aName,
  bName,
  height = 240,
  markerDay,
  markerLabel,
}: {
  data: { day: string; a: number; b: number }[];
  aName: string;
  bName: string;
  height?: number;
  /** Day (matching a data point) to mark with a vertical dashed line. */
  markerDay?: string;
  markerLabel?: string;
}) {
  if (data.length === 0) {
    return <ChartEmptyState />;
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid stroke="var(--viz-grid)" vertical={false} />
        <XAxis
          dataKey="day"
          tickFormatter={formatMonthDay}
          tick={AXIS_TICK}
          axisLine={{ stroke: "var(--viz-baseline)" }}
          tickLine={false}
          minTickGap={32}
        />
        <YAxis
          tickFormatter={(v: number) => formatChartLabel(v)}
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          width={52}
        />
        <Tooltip
          content={<ChartTooltip labelFormatter={formatMonthDay} />}
          cursor={{ stroke: "var(--viz-baseline)", strokeWidth: 1 }}
        />
        <Legend
          verticalAlign="top"
          // Extra headroom when a marker is present: its label renders in
          // the gap below the legend, above the plot area, so the data
          // lines can never cover it.
          height={markerDay ? 46 : 28}
          iconType="plainline"
          formatter={legendText}
        />
        {markerDay ? (
          <ReferenceArea
            x1={data[0].day}
            x2={markerDay}
            fill="var(--viz-muted)"
            fillOpacity={0.07}
            stroke="none"
          />
        ) : null}
        {markerDay ? (
          <ReferenceLine
            x={markerDay}
            stroke="var(--viz-muted)"
            strokeDasharray="4 4"
            label={{
              value: markerLabel,
              position: "top",
              fill: "var(--viz-muted)",
              fontSize: 11,
            }}
          />
        ) : null}
        <Line
          name={aName}
          dataKey="a"
          type="monotone"
          isAnimationActive={false}
          stroke="var(--viz-series-1)"
          strokeWidth={2}
          strokeLinecap="round"
          dot={false}
          activeDot={{ r: 4, stroke: "var(--viz-surface)", strokeWidth: 2 }}
        />
        <Line
          name={bName}
          dataKey="b"
          type="monotone"
          isAnimationActive={false}
          stroke="var(--viz-series-2)"
          strokeWidth={2}
          strokeLinecap="round"
          dot={false}
          activeDot={{ r: 4, stroke: "var(--viz-surface)", strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
